/**
 * Marking criteria — the three FIXED sets, and the seven capture tap-tags.
 *
 * Authority: `docs/specs/control-centre/05a-marking-criteria-reference.md`, extracted
 * 2026-08-11 from the live TMS marking UI and the generated Performance Report PDFs.
 * Where `05-reporting-marking.md` §B disagrees with 05a, **05a wins**.
 *
 * **Ruling (Alfred, 2026-08-11): the criteria are FIXED, not configurable.** The sets
 * change roughly every 5–10 years, so there is no criteria editor, no per-tournament
 * criteria configuration and no seeding step. They live here as constants, used by both
 * the marking screen and the report generator. If FIH revises them, that is a code change.
 * This supersedes the earlier D40 instruction to ship the sets as configurable data; the
 * `stable_key` discipline of D40 survives intact — `criterion_stable_key` remains the only
 * thing captured evidence ever stores.
 *
 * **Ruling UM-Q9 (Alfred, 2026-08-11):** the seven capture tap-tags are likewise fixed at
 * build and derived from the Umpire criteria vocabulary, 1:1 by `criterion_stable_key`.
 * No tag editor, no configurability.
 *
 * British English throughout, per the standing copy rule.
 */
/* ────────────────────────────────────────────────────────────────────────────
 * 1. Stable-key derivation
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Derive a `criterion_stable_key` from a display name: lower-cased, `&` treated as a word
 * break, every other non-alphanumeric run collapsed to a single hyphen.
 *
 *   `Rules & Decision-Making`      → `rules-decision-making`
 *   `Professionalism&Mindset`      → `professionalism-mindset`
 *   `Knowledge&Understanding of Game` → `knowledge-understanding-of-game`
 *
 * The `Rules&Regulations` (UM) and `Rules & Regulations` (TO) spellings deliberately
 * collapse to the same key — keys are unique **within** a set, not across sets.
 */
export function criterionStableKey(name) {
    return name
        .toLowerCase()
        .replace(/&/g, " ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}
/** Build a set from `[name, weight]` pairs, deriving keys and sort order. */
function buildSet(rows) {
    return Object.freeze(rows.map(([name, weight], index) => Object.freeze({
        criterion_stable_key: criterionStableKey(name),
        name,
        weight,
        sort_order: index + 1,
    })));
}
/* ────────────────────────────────────────────────────────────────────────────
 * 2. The three fixed sets (05a)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * **Umpire — 10 criteria.** Four heavy at 35 (Σ140) + five light at 12 (Σ60) = 200,
 * plus `Video Umpire` at **weight 0**.
 *
 * `Video Umpire` is marked on the same slider and **printed on the report**, but carries
 * weight 0, so the weighted formula excludes it from the Performance Outcome naturally.
 * It stays in the set and in every report rendering — do not filter it out.
 */
export const UMPIRE_CRITERIA = buildSet([
    ["Rules & Decision-Making", 35],
    ["Management & Control", 35],
    ["Flow & Timing", 35],
    ["Presentation", 35],
    ["Professionalism&Mindset", 12],
    ["Communication&Relationships", 12],
    ["Teamwork Skills", 12],
    ["English Language", 12],
    ["Physical Fitness", 12],
    ["Video Umpire", 0],
]);
/** **Umpires Manager — 9 criteria.** 4 × 35 + 5 × 12 = 200. */
export const UMPIRE_MANAGER_CRITERIA = buildSet([
    ["Rules&Regulations", 35],
    ["Knowledge&Understanding of Game", 35],
    ["Umpire Assessment&Coaching", 35],
    ["Competition Administration", 35],
    ["Professionalism&Mindset", 12],
    ["Communication&Relationships", 12],
    ["Teamwork Skills", 12],
    ["Technology", 12],
    ["English Language", 12],
]);
/**
 * **Technical Official (Judges included, per R-2′) and ATD — 8 criteria.**
 * 4 × 35 + 4 × **15** = 200.
 *
 * The light criteria weigh **15 here, not 12** — this set has one fewer light criterion,
 * so the weights differ from the Umpire and UM sets. Do not "normalise" this.
 */
export const TECHNICAL_OFFICIAL_AND_ATD_CRITERIA = buildSet([
    ["Rules & Regulations", 35],
    ["Technical Table Skills", 35],
    ["Match Management", 35],
    ["Competition Administration", 35],
    ["Professionalism&Mindset", 15],
    ["Communication&Relationships", 15],
    ["Teamwork Skills", 15],
    ["English Language", 15],
]);
/** Every fixed set, by grading-role key. */
export const MARKING_CRITERIA_SETS = Object.freeze({
    UMPIRE: UMPIRE_CRITERIA,
    UMPIRE_MANAGER: UMPIRE_MANAGER_CRITERIA,
    TECHNICAL_OFFICIAL_AND_ATD: TECHNICAL_OFFICIAL_AND_ATD_CRITERIA,
});
/** Every set totals 200 for outcome purposes. */
export const CRITERIA_WEIGHT_TOTAL = 200;
/**
 * Grading-role key for a roster role. **The TD receives no marks and no report**
 * (ruling R2′), and the Tournament Administrator is not marked either — both return null.
 */
export function gradingRoleKeyForRole(role) {
    switch (role) {
        case "Umpire":
            return "UMPIRE";
        case "UM":
            return "UMPIRE_MANAGER";
        case "ATD":
        case "TO":
        case "Judge":
            return "TECHNICAL_OFFICIAL_AND_ATD";
        case "TD":
        case "Tournament Administrator":
            return null;
    }
}
/** Look a criterion up by stable key within a set. */
export function findCriterion(criteria, stableKey) {
    return criteria.find((criterion) => criterion.criterion_stable_key === stableKey);
}
/* ────────────────────────────────────────────────────────────────────────────
 * 3. The weighted outcome
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The Performance Outcome: `Math.round(Σ(mark × weight) / Σweight)`.
 *
 * Zero-weight criteria (`Video Umpire`) contribute nothing to either sum, so they drop out
 * of the result by arithmetic rather than by a filter — which is why the criterion stays in
 * the set and on the printed report.
 *
 * Rounding is `Math.round`: 71.25 → 71, 84.5 → 85.
 * Returns null when nothing weighted has been marked (denominator 0).
 */
export function weightedOutcome(entries) {
    let numerator = 0;
    let denominator = 0;
    for (const { mark, weight } of entries) {
        numerator += mark * weight;
        denominator += weight;
    }
    if (denominator === 0)
        return null;
    return Math.round(numerator / denominator);
}
/**
 * The Performance Outcome for one official's whole-tournament marking (ruling R1′ — one
 * marking per official per tournament, no aggregation anywhere).
 *
 * A criterion is excluded from **both** sums when it is marked `na` or has no value yet;
 * an `na` therefore removes its weight from the denominator, exactly as the TMS form does.
 * Marks whose key is not in the set are ignored.
 */
export function computePerformanceOutcome(criteria, marks) {
    const entries = [];
    for (const criterion of criteria) {
        const mark = marks.find((m) => m.criterion_stable_key === criterion.criterion_stable_key);
        if (mark === undefined || mark.na === true || mark.value === null)
            continue;
        entries.push({ mark: mark.value, weight: criterion.weight });
    }
    return weightedOutcome(entries);
}
/** Σweight across a set — 200 for all three, and the marking panel's live denominator. */
export function sumWeights(criteria) {
    return criteria.reduce((total, criterion) => total + criterion.weight, 0);
}
/* ────────────────────────────────────────────────────────────────────────────
 * 4. The seven capture tap-tags (ruling UM-Q9)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The UM app's capture screen carries **exactly these seven tap-tags**, fixed at build and
 * mapped 1:1 onto Umpire criteria by `criterion_stable_key`, so a tagged note grounds the
 * end-of-tournament marks discussion and the AI report draft without translation.
 *
 * Two labels are deliberately shorter than the criterion they file into —
 * `Communication` → `Communication&Relationships`, `Fitness` → `Physical Fitness`.
 * The label is display only; the stable key is the link.
 */
export const CAPTURE_TAP_TAGS = Object.freeze([
    ["Rules & Decision-Making", "rules-decision-making"],
    ["Management & Control", "management-control"],
    ["Flow & Timing", "flow-timing"],
    ["Presentation", "presentation"],
    ["Communication", "communication-relationships"],
    ["Fitness", "physical-fitness"],
    ["Video Umpire", "video-umpire"],
].map(([label, criterionKey], index) => Object.freeze({
    tag_id: criterionStableKey(label),
    label,
    criterion_stable_key: criterionKey,
    sort_order: index + 1,
})));
/** The Umpire criterion a tap-tag files into. Never undefined for a shipped tag. */
export function criterionForTapTag(tag) {
    return findCriterion(UMPIRE_CRITERIA, tag.criterion_stable_key);
}
/**
 * The tap-tag with this id, or `undefined`.
 *
 * The capture route validates every inbound `tag_ids` entry through this function, so a
 * client cannot invent a tag: the seven are fixed at build (UM-Q9) and an eighth is a code
 * change, not a payload. It is the same lookup the capture screen renders from, which is
 * what stops the palette and the validator drifting apart.
 */
export function findCaptureTapTag(tagId) {
    return CAPTURE_TAP_TAGS.find((tag) => tag.tag_id === tagId);
}
/* ────────────────────────────────────────────────────────────────────────────
 * 5. TD Quick Note categories (spec 01 §12 step 02)
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The category a TD quick note is filed under — spec 01 §12's step 02, *"Choose a grading
 * category — role-specific"*, and the vocabulary `td_note.criterion_stable_key` was parked for.
 *
 * **The list is the marking criteria and nothing else.** A category here is one of the fixed
 * criteria above, named by `criterion_stable_key`, so a note filed at pitch-side and a mark
 * entered on the reporting screen are talking about the same thing without translation — which
 * is exactly what migration 037's column comment promises: *"the seam that lets Quick Notes, TD
 * Feedback and Report Readiness stay one dataset"*. There is no separate category catalogue to
 * drift, and a fourth category is a code change to the sets above, not a payload.
 *
 * **`General` is the absence of a criterion, stored as NULL.** Migration 037 wrote the column
 * nullable and described an unwritten key as a note the profile *"reads back uncategorised"*,
 * so `General` needs no sentinel: it IS the null the column was designed around, and every note
 * captured before this feature existed is already a General note without a backfill. A sentinel
 * string would have meant two spellings of uncategorised — the old rows' null and the new rows'
 * marker — with every reader obliged to know both. `General` is also the default, and is where
 * the draft tournament report summary will read from: a note the TD did not file against a
 * criterion is a note about the official at large, which is the summary's subject.
 */
export const TD_NOTE_GENERAL_LABEL = "General";
/**
 * Every `criterion_stable_key` any set uses, for validating an inbound category.
 *
 * The union across all three sets rather than the one set the subject's role is marked on: keys
 * are unique WITHIN a set and not across them (`professionalism-mindset` is in all three), so a
 * per-role check on the wire would refuse nothing a per-role PICKER can produce while adding a
 * roster lookup to every write. The picker is what makes a category role-appropriate — see
 * {@link tdNoteCategoriesForRole} — and this is what makes an invented key impossible, the same
 * division of labour {@link findCaptureTapTag} keeps for the seven tap-tags.
 */
export const TD_NOTE_CATEGORY_KEYS = new Set(Object.values(MARKING_CRITERIA_SETS).flatMap((criteria) => criteria.map((criterion) => criterion.criterion_stable_key)));
/** True for a key some marking set defines. `General` is null and is not a key — see above. */
export function isTdNoteCategoryKey(key) {
    return TD_NOTE_CATEGORY_KEYS.has(key);
}
/**
 * The categories offered for one roster role: that role's marking set, or none at all.
 *
 * `TD` and `Tournament Administrator` are marked on nothing (ruling R2′), so they are offered
 * no criteria and every note about them is General. That is a real state and not an oversight —
 * a TD does keep notes on his own Tournament Administrator.
 */
export function tdNoteCategoriesForRole(role) {
    const key = gradingRoleKeyForRole(role);
    return key === null ? [] : MARKING_CRITERIA_SETS[key];
}
/**
 * A criterion name as a HUMAN reads it: TMS's cramped ampersands are given their spaces
 * (`Professionalism&Mindset` → `Professionalism & Mindset`), and nothing else changes.
 *
 * DISPLAY-side only, by ruling (Alfred, 2026-09-03): the `name` fields above stay verbatim
 * from TMS — the marking screen mirrors the TMS form and the push/copy paths print TMS's own
 * spellings — but a surface that is ours alone (the Quick Note category picker, the note
 * chips) owes the reader ordinary typography. Stable keys are unaffected either way:
 * `criterionStableKey` already treats `&` as a word break, so both spellings of a name
 * derive the same key.
 */
export function tidyCriterionDisplayName(name) {
    return name.replace(/\s*&\s*/g, " & ");
}
/**
 * What a stored category reads as on the profile: `General` for a note with no key, and
 * otherwise the criterion's display name — tidied for reading (see
 * {@link tidyCriterionDisplayName}), where the marking screen keeps TMS's verbatim spelling.
 *
 * The first set holding the key wins, which is safe because a key shared across sets is shared
 * because it is the SAME criterion under the same name — `Professionalism&Mindset` is spelled
 * once. An unrecognised key falls back to itself rather than to `General`: no route can store
 * one, so a key that turns up unknown is a criteria revision the reader should see, not a note
 * quietly relabelled as uncategorised.
 */
export function tdNoteCategoryLabel(criterionStableKey) {
    if (criterionStableKey === null)
        return TD_NOTE_GENERAL_LABEL;
    for (const criteria of Object.values(MARKING_CRITERIA_SETS)) {
        const criterion = findCriterion(criteria, criterionStableKey);
        if (criterion !== undefined)
            return tidyCriterionDisplayName(criterion.name);
    }
    return criterionStableKey;
}
//# sourceMappingURL=marking-criteria.js.map